import { Component, EventEmitter, Input, Output, ViewChild, TemplateRef, OnInit, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { AlertModalService } from '../../services/alert-modal.service';

@Component({
  selector: 'app-password-management',
  templateUrl: './password-management.component.html',
  styleUrls: ['./password-management.component.scss']
})
export class PasswordManagementComponent implements OnInit {
  @ViewChild('PasswordModalContent', { static: true }) passwordModalContent!: TemplateRef<any>;
  
  @Input() isPasswordSet: boolean = false;
  @Input() pathwayId: string = '';
  @Input() showPasswordManagement: boolean = true;
  @Input() selectedPrivacyOption: string = '';
  @Input() isCreateMode: boolean = false; // New input to distinguish create vs edit mode
  
  @Output() passwordSet = new EventEmitter<{ pathwayId: string, password: string }>();
  @Output() passwordRemoved = new EventEmitter<string>();
  @Output() passwordChanged = new EventEmitter<{ pathwayId: string, password: string }>();

  tangramPassword: string = '';
  confirmPassword: string = '';
  passwordError: string = '';
  private hasOpenedModal: boolean = false; // Track if modal has been opened in this session
  
  // Password visibility properties
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  constructor(
    private modalService: NgbModal,
    private toasterService: ToastrService,
    private alertModalService: AlertModalService,
  ) {}

  ngOnInit() {
    // Auto-open modal if password protected is selected and no password is set
    // In create mode, only open if password is not set
    if (this.selectedPrivacyOption === 'password-protected' && 
        (!this.isPasswordSet && this.isCreateMode) && 
        !this.hasOpenedModal) {
      setTimeout(() => {
        this.openPasswordModal();
        this.hasOpenedModal = true;
      }, 100);
    }

  }

  ngOnChanges(_changes: SimpleChanges) {
    // Don't auto-open modal in create mode - let ngOnInit handle it
    // if (this.isCreateMode) {
    //   return;
    // }
    // // console.log('this.isCreateMode: ', this.isCreateMode);
    // // Auto-open modal when privacy option changes to password protected (edit mode only)
    // if (changes['selectedPrivacyOption'] && 
    //     changes['selectedPrivacyOption'].currentValue === 'password-protected' && 
    //     !this.isPasswordSet && 
    //     !this.hasOpenedModal) {
    //   setTimeout(() => {
    //     this.openPasswordModal();
    //     this.hasOpenedModal = true;
    //   }, 100);
    // }
  }

  /**
   * Open password modal
   */
  openPasswordModal() {
    this.passwordError = '';
    this.tangramPassword = '';
    this.confirmPassword = '';
    this.modalService.open(this.passwordModalContent, { centered: true });
  }

  /**
   * Set password for Tangram
   */
  setPassword(modal: any) {
    this.passwordError = '';
    
    if (!this.tangramPassword) {
      this.passwordError = 'Please enter a password';
      return;
    }
    
    if (this.tangramPassword.length < 8) {
      this.passwordError = 'Password must be at least 8 characters long';
      return;
    }
    
    // Check for at least one special character
    const specialCharRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
    if (!specialCharRegex.test(this.tangramPassword)) {
      this.passwordError = 'Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)';
      return;
    }
    
    if (this.tangramPassword !== this.confirmPassword) {
      this.passwordError = 'Passwords do not match';
      return;
    }
    
    // Emit password set event
    this.passwordSet.emit({
      pathwayId: this.pathwayId,
      password: this.tangramPassword
    });
    
    this.hasOpenedModal = false; // Reset flag so modal can open again if needed
    modal.close('Password Set');
  }

  /**
   * Remove password from Tangram
   */
  removePassword() {
    this.alertModalService.confirm({
      message: 'Are you sure you want to remove the password protection from this Tangram?',
      cancelText: 'No',
      confirmText: 'Yes, Remove',
    }).subscribe(confirmed => { if (confirmed) this.passwordRemoved.emit(this.pathwayId); });
  }

  /**
   * Change password (same as set password but different event)
   */
  changePassword(modal: any) {
    this.passwordError = '';
    
    if (!this.tangramPassword) {
      this.passwordError = 'Please enter a password';
      return;
    }
    
    if (this.tangramPassword.length < 8) {
      this.passwordError = 'Password must be at least 8 characters long';
      return;
    }
    
    // Check for at least one special character
    const specialCharRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
    if (!specialCharRegex.test(this.tangramPassword)) {
      this.passwordError = 'Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)';
      return;
    }
    
    if (this.tangramPassword !== this.confirmPassword) {
      this.passwordError = 'Passwords do not match';
      return;
    }
    
    // Emit password changed event
    this.passwordChanged.emit({
      pathwayId: this.pathwayId,
      password: this.tangramPassword
    });    
    this.hasOpenedModal = false; // Reset flag so modal can open again if needed
    modal.close('Password Changed');
  }

  /**
   * Get the appropriate button text based on password status
   */
  getPasswordButtonText(): string {
    // In create mode, always show "Set Password" even if password is set
    if (this.isCreateMode || (!this.isPasswordSet && this.isCreateMode)) {
      return 'Set Password';
    }
    return this.isPasswordSet ? 'Change Password' : 'Set Password';
  }

  /**
   * Get the appropriate modal title based on password status
   */
  getModalTitle(): string {
    // In create mode, always show "Set Password" even if password is set
    if (this.isCreateMode || (!this.isPasswordSet && this.isCreateMode)) {
      return 'Set Password';
    }
    return this.isPasswordSet ? 'Change Password' : 'Set Password';
  }

  /**
   * Toggle password visibility
   */
  togglePasswordVisibility(field: 'password' | 'confirm'): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else if (field === 'confirm') {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

}
